import { useCallback, useEffect, useRef, useState } from "react";
import {
    MAX_ATTACHMENTS,
    readAsDataUri,
    validateAttachment,
    validatePastedImage,
} from "./attachmentRules";

/**
 * Gönderilmeyi bekleyen eklerin yönetimi: seçme, panodan yapıştırma, kaldırma.
 * Talep oluşturma ve yanıt kutusu aynı davranışı paylaşsın diye hook olarak ayrıldı.
 *
 * İki taşıma yolu vardır ve listede birlikte dururlar:
 * - **Dosya seçiciden gelenler** multipart olarak gider; önizleme object URL'dir
 *   (dosyanın kendisi belleğe kopyalanmaz) ve listeden çıkınca serbest bırakılır.
 * - **Panodan yapıştırılanlar** base64 (data URI) olarak gider; ekran görüntüsünün
 *   dosyası olmadığı için içeriğin kendisi taşınır, önizleme de aynı data URI'dir.
 */
export const useTicketAttachments = (onError) => {
    const [items, setItems] = useState([]);
    const itemsRef = useRef(items);
    itemsRef.current = items;

    const revoke = (item) => {
        if (item?.previewUrl && !item.pasted) URL.revokeObjectURL(item.previewUrl);
    };

    const add = useCallback((fileList) => {
        const incoming = Array.from(fileList || []);
        if (incoming.length === 0) return;

        setItems(prev => {
            const accepted = [];
            for (const file of incoming) {
                if (prev.length + accepted.length >= MAX_ATTACHMENTS) {
                    onError?.(`En fazla ${MAX_ATTACHMENTS} dosya eklenebilir.`);
                    break;
                }
                const problem = validateAttachment(file);
                if (problem) {
                    onError?.(problem);
                    continue;
                }
                accepted.push({
                    id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
                    name: file.name,
                    size: file.size,
                    file,
                    pasted: false,
                    previewUrl: URL.createObjectURL(file),
                });
            }
            return accepted.length ? [...prev, ...accepted] : prev;
        });
    }, [onError]);

    /** Panodan gelen görselleri base64'e çevirip listeye ekler. */
    const addPasted = useCallback(async (fileList) => {
        const incoming = Array.from(fileList || []);
        for (const file of incoming) {
            if (itemsRef.current.length >= MAX_ATTACHMENTS) {
                onError?.(`En fazla ${MAX_ATTACHMENTS} dosya eklenebilir.`);
                return;
            }
            const problem = validatePastedImage(file);
            if (problem) {
                onError?.(problem);
                continue;
            }
            try {
                const dataUri = await readAsDataUri(file);
                const item = {
                    id: `pasted-${file.size}-${Math.random()}`,
                    name: "Yapıştırılan görsel",
                    size: file.size,
                    dataUri,
                    pasted: true,
                    // Data URI hem önizlemede hem gönderimde kullanılır; serbest bırakma gerekmez.
                    previewUrl: dataUri,
                };
                itemsRef.current = [...itemsRef.current, item];
                setItems(itemsRef.current);
            } catch {
                onError?.("Yapıştırılan görsel okunamadı.");
            }
        }
    }, [onError]);

    const remove = useCallback((id) => {
        setItems(prev => {
            revoke(prev.find(i => i.id === id));
            return prev.filter(i => i.id !== id);
        });
    }, []);

    const clear = useCallback(() => {
        itemsRef.current.forEach(revoke);
        setItems([]);
    }, []);

    /** Ctrl+V / Cmd+V ile panodaki ekran görüntüsünü ekler. Metin yapıştırma etkilenmez. */
    const handlePaste = useCallback((event) => {
        const files = Array.from(event.clipboardData?.files || []);
        if (files.length === 0) return;
        event.preventDefault();
        addPasted(files);
    }, [addPasted]);

    // Sayfadan ayrılırken kalan önizlemeleri serbest bırak
    useEffect(() => () => {
        itemsRef.current.forEach(revoke);
    }, []);

    return {
        items,
        add,
        remove,
        clear,
        handlePaste,
        /** multipart ile gidecek dosyalar */
        files: items.filter(i => !i.pasted).map(i => i.file),
        /** base64 ile gidecek yapıştırılmış görseller */
        pastedImages: items.filter(i => i.pasted).map(i => i.dataUri),
    };
};
